import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type FirestoreError,
} from 'firebase/firestore'

import type {
  Card,
  CardUpdate,
  Deck,
  DeckUpdate,
  NewCardInput,
  NewDeckInput,
} from '@study-decks/shared'

import { getDb } from './firebase.js'

const MAX_CARDS = 490

type SnapshotLike = { id: string; data(): Record<string, unknown> }

interface TimestampLike {
  toDate(): Date
}

export class DeckApiError extends Error {
  override readonly name = 'DeckApiError'

  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

function toIso(value: unknown): string {
  if (value instanceof Timestamp) return value.toDate().toISOString()
  if (typeof value === 'string') return value
  if (isTimestampLike(value)) {
    return value.toDate().toISOString()
  }
  return new Date().toISOString()
}

function isTimestampLike(value: unknown): value is TimestampLike {
  return (
    value !== null &&
    typeof value === 'object' &&
    'toDate' in value &&
    typeof value.toDate === 'function'
  )
}

function fromIso(value: string) {
  return Timestamp.fromDate(new Date(value))
}

function stringValue(value: unknown) {
  return typeof value === 'string' ? value : ''
}

export function deckFromSnapshot(snapshot: SnapshotLike): Deck {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    title: stringValue(data.title),
    description: stringValue(data.description),
    ownerId: stringValue(data.ownerId),
    ownerRole: data.ownerRole === 'student' ? 'student' : 'teacher',
    classId: typeof data.classId === 'string' ? data.classId : null,
    visibility:
      data.visibility === 'class' || data.visibility === 'domain' ? data.visibility : 'private',
    subject: typeof data.subject === 'string' ? data.subject : null,
    tags: Array.isArray(data.tags)
      ? data.tags.filter((tag): tag is string => typeof tag === 'string')
      : [],
    cardCount: typeof data.cardCount === 'number' ? data.cardCount : 0,
    createdAt: toIso(data.createdAt),
    updatedAt: toIso(data.updatedAt),
    status: data.status === 'flagged' || data.status === 'unpublished' ? data.status : 'published',
    flaggedBy: Array.isArray(data.flaggedBy)
      ? data.flaggedBy.filter((uid): uid is string => typeof uid === 'string')
      : [],
    moderatedBy: typeof data.moderatedBy === 'string' ? data.moderatedBy : null,
  }
}

function cardFromSnapshot(snapshot: SnapshotLike): Card {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    term: stringValue(data.term),
    definition: stringValue(data.definition),
    termAudioUrl: typeof data.termAudioUrl === 'string' ? data.termAudioUrl : null,
    definitionAudioUrl:
      typeof data.definitionAudioUrl === 'string' ? data.definitionAudioUrl : null,
    imageUrl: typeof data.imageUrl === 'string' ? data.imageUrl : null,
    position: typeof data.position === 'number' ? data.position : 0,
    createdAt: toIso(data.createdAt),
  }
}

function errorCode(err: unknown): string {
  const code = (err as Partial<FirestoreError>).code
  if (code === 'permission-denied') return 'PERMISSION_DENIED'
  if (code === 'not-found') return 'NOT_FOUND'
  if (code === 'unavailable') return 'UNAVAILABLE'
  if (code === 'failed-precondition') return 'FAILED_PRECONDITION'
  return 'REQUEST_FAILED'
}

function errorMessage(code: string): string {
  if (code === 'PERMISSION_DENIED') return "You don't have access to this deck."
  if (code === 'NOT_FOUND') return 'This deck no longer exists.'
  if (code === 'DECK_FULL') return 'Decks are capped at 490 cards.'
  if (code === 'UNAVAILABLE') return "Couldn't reach the database — check your connection."
  return 'Something went wrong — please try again.'
}

function normalizeError(err: unknown): DeckApiError {
  if (err instanceof DeckApiError) return err
  const code = errorCode(err)
  return new DeckApiError(code, errorMessage(code))
}

function rejectWithDeckError<T>(err: unknown): Promise<T> {
  return Promise.reject(normalizeError(err))
}

function deckRef(deckId: string) {
  return doc(getDb(), 'decks', deckId)
}

function cardsCollectionRef(deckId: string) {
  return collection(getDb(), 'decks', deckId, 'cards')
}

export async function listMyDecks(uid: string): Promise<Deck[]> {
  try {
    const decksQuery = query(collection(getDb(), 'decks'), where('ownerId', '==', uid))
    const snapshot = await getDocs(decksQuery)
    return snapshot.docs
      .map(deckFromSnapshot)
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function getDeck(deckId: string): Promise<Deck | null> {
  try {
    const snapshot = await getDoc(deckRef(deckId))
    return snapshot.exists() ? deckFromSnapshot(snapshot) : null
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function listCards(deckId: string): Promise<Card[]> {
  try {
    const cardsQuery = query(cardsCollectionRef(deckId), orderBy('position', 'asc'))
    const snapshot = await getDocs(cardsQuery)
    return snapshot.docs.map(cardFromSnapshot)
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function listPublishedClassDecks(classId: string): Promise<Deck[]> {
  try {
    const decksQuery = query(
      collection(getDb(), 'decks'),
      where('classId', '==', classId),
      where('visibility', '==', 'class'),
      where('status', '==', 'published'),
    )
    const snapshot = await getDocs(decksQuery)
    return snapshot.docs
      .map(deckFromSnapshot)
      .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }))
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function createDeck(input: NewDeckInput, ownerUid: string): Promise<Deck> {
  const ref = doc(collection(getDb(), 'decks'))
  const now = new Date().toISOString()
  const deck: Deck = {
    id: ref.id,
    title: input.title,
    description: input.description,
    ownerId: ownerUid,
    ownerRole: 'teacher',
    classId: input.classId,
    visibility: input.visibility,
    subject: input.subject,
    tags: input.tags,
    cardCount: 0,
    createdAt: now,
    updatedAt: now,
    status: 'published',
    flaggedBy: [],
    moderatedBy: null,
  }

  try {
    await setDoc(ref, {
      ...deck,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return deck
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function createStudentDeck(input: NewDeckInput, ownerUid: string): Promise<Deck> {
  const ref = doc(collection(getDb(), 'decks'))
  const now = new Date().toISOString()
  const deck: Deck = {
    id: ref.id,
    title: input.title,
    description: input.description,
    ownerId: ownerUid,
    ownerRole: 'student',
    classId: input.classId,
    visibility: input.visibility,
    subject: input.subject,
    tags: input.tags,
    cardCount: 0,
    createdAt: now,
    updatedAt: now,
    status: 'published',
    flaggedBy: [],
    moderatedBy: null,
  }

  try {
    await setDoc(ref, {
      ...deck,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return deck
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function updateDeck(deckId: string, patch: DeckUpdate): Promise<void> {
  try {
    await updateDoc(deckRef(deckId), {
      ...patch,
      updatedAt: serverTimestamp(),
    })
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function createCard(
  deckId: string,
  input: NewCardInput,
  currentCardCount: number,
): Promise<Card> {
  if (currentCardCount >= MAX_CARDS) {
    throw new DeckApiError('DECK_FULL', errorMessage('DECK_FULL'))
  }

  const db = getDb()
  const ref = doc(cardsCollectionRef(deckId))
  const now = new Date().toISOString()
  const card: Card = {
    id: ref.id,
    term: input.term,
    definition: input.definition,
    termAudioUrl: null,
    definitionAudioUrl: null,
    imageUrl: null,
    position: currentCardCount + 1,
    createdAt: now,
  }

  try {
    const batch = writeBatch(db)
    batch.set(ref, {
      ...card,
      createdAt: serverTimestamp(),
    })
    batch.update(deckRef(deckId), {
      cardCount: currentCardCount + 1,
      updatedAt: serverTimestamp(),
    })
    await batch.commit()
    return card
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function updateCard(deckId: string, cardId: string, patch: CardUpdate): Promise<void> {
  try {
    await updateDoc(doc(getDb(), 'decks', deckId, 'cards', cardId), patch)
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function deleteCard(
  deckId: string,
  cardId: string,
  currentCardCount: number,
): Promise<void> {
  const db = getDb()
  try {
    const batch = writeBatch(db)
    batch.delete(doc(db, 'decks', deckId, 'cards', cardId))
    batch.update(deckRef(deckId), {
      cardCount: Math.max(0, currentCardCount - 1),
      updatedAt: serverTimestamp(),
    })
    await batch.commit()
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export async function reorderCards(deckId: string, cardIds: string[]): Promise<void> {
  if (cardIds.length > MAX_CARDS) {
    throw new DeckApiError('DECK_FULL', errorMessage('DECK_FULL'))
  }

  const db = getDb()
  try {
    const batch = writeBatch(db)
    cardIds.forEach((cardId, index) => {
      batch.update(doc(db, 'decks', deckId, 'cards', cardId), { position: index + 1 })
    })
    batch.update(deckRef(deckId), { updatedAt: serverTimestamp() })
    await batch.commit()
  } catch (err) {
    return rejectWithDeckError(err)
  }
}

export function listenToDeck(deckId: string, onChange: (deck: Deck | null) => void): () => void {
  return onSnapshot(
    deckRef(deckId),
    (snapshot) => onChange(snapshot.exists() ? deckFromSnapshot(snapshot) : null),
    (err) => {
      throw normalizeError(err)
    },
  )
}

export function listenToCards(deckId: string, onChange: (cards: Card[]) => void): () => void {
  const cardsQuery = query(cardsCollectionRef(deckId), orderBy('position', 'asc'))
  return onSnapshot(
    cardsQuery,
    (snapshot) => onChange(snapshot.docs.map(cardFromSnapshot)),
    (err) => {
      throw normalizeError(err)
    },
  )
}

export function isoToTimestamp(value: string): Timestamp {
  return fromIso(value)
}
